import { useMemo, useState } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { ArrowRight } from 'svgIcons'
import { useCurrentUserQuery } from 'generated/graphql'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import { mobileBreakPoint } from 'app.styled/media'
import LoginAlert from 'components/LoginAlert'
import LineTabs, {
  TabType,
} from '../../../../app.components/tabs/LineTabs/LineTabs'
import ChannelSearchBar from '../ChannelSearchbar'

interface IFChannelNavbarDesktopProps {
  sticky?: boolean
  searchBar?: 'visible' | 'unvisible' | 'visible-when-stucked'
  initialTab?: number
  disableShadow?: boolean
  className?: string
}

const ChannelNavbarDesktop: React.FC<IFChannelNavbarDesktopProps> = ({
  sticky = false,
  searchBar = false,
  initialTab,
  className,
}) => {
  const router = useRouter()
  const [loginModalOpened, setLoginModalOpened] = useState(false)
  const currentUserQuery = useCurrentUserQuery()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()

  const tabs = [
    {
      id: 0,
      name: '채널홈',
      size: `60px`,
      href: {
        pathname: getWebviewHref('/channel', isWebview),
        query: { tab: 'home' },
      },
    },
    {
      id: 1,
      name: '내 관심 소식',
      size: `103px`,
      href: {
        pathname: getWebviewHref('/channel', isWebview),
        query: { tab: 'interest' },
      },
    },
  ]

  const visibleSearchBar = useMemo(() => {
    if (searchBar === 'visible') return true
    if (searchBar === 'unvisible') return false
    return searchBar && sticky
  }, [searchBar, sticky])

  const tabActive =
    router.pathname === '/channel' || router.pathname === '/webview/channel'

  const checkTab = (menu: TabType) => {
    if (menu.id === 1 && !currentUserQuery.data?.currentUser) {
      setLoginModalOpened(true)
      return false
    }
    return true
  }

  return (
    <StyledWrapper className={clsx(className, { sticky })}>
      <div className="nav-content">
        <div className="lt">
          <LineTabs
            className="line-tab"
            data={tabs}
            initialTab={initialTab ? tabs[initialTab] : undefined}
            active={tabActive}
            disableGlider={isMobile}
            checkTab={checkTab}
          />
          {currentUserQuery.data?.currentUser?.channel && (
            <Link
              href={getWebviewHref(
                `/channel/${currentUserQuery.data.currentUser.channel.url}`,
                isWebview,
              )}
            >
              <a className="tab-my-channel">
                내 채널 바로가기
                <ArrowRight className="arrow-right" />
              </a>
            </Link>
          )}
        </div>
        <div
          className={`search-bar-wrapper desktop ${
            visibleSearchBar ? 'visible' : ''
          }`}
        >
          <ChannelSearchBar className="search-bar" />
        </div>
      </div>
      <LoginAlert
        DialogProps={{
          open: loginModalOpened,
        }}
        onRequestClose={() => setLoginModalOpened(false)}
      />
    </StyledWrapper>
  )
}

export default ChannelNavbarDesktop

const StyledWrapper = styled.div`
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  transition: box-shadow 0.3s ease-in-out;
  &.sticky {
    position: sticky;
    top: 61px;
  }
  .nav-content {
    width: 100%;
    max-width: 1148px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 auto;
    padding: 0 24px 0 14px;
    .lt {
      display: flex;
      .tab {
        :hover {
          color: #01a0ff;
        }
      }
      .tab-my-channel {
        font-size: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        white-space: nowrap;
        gap: 2px;
        padding-left: 5px;
        font-weight: 500;
        .arrow-right {
          font-size: 13px;
          padding-top: 1px;
        }
      }
    }
    .search-bar-wrapper {
      opacity: 0;
      padding: 8px 0;
      width: 100%;
      max-width: 780px;
      transform: translate(0px, -60px);
      transition: transform 0.3s ease, opacity 0.3s ease, max-width 0.3s ease;
      &.visible {
        opacity: 1;
        max-width: 780px;
        transform: translate(0px, 0px);
      }
      .search-bar {
        height: 40px;
        &:focus-within {
          border: 1px solid #01a0ff;
        }
      }
    }
  }
  ${mobileBreakPoint} {
    background-color: #fafafa;
    &.sticky {
      top: 0;
    }
    .nav-content {
      gap: 0;
      padding: 0 20px;
      height: 50px;
      .lt {
        .tab {
          font-size: 15px;
          margin-left: -13px;
        }
        .tab-my-channel {
          font-size: 15px;
          gap: 0;
          padding: 0;
          margin-left: -4px;
          .arrow-right {
            font-size: 12px;
          }
        }
      }
      .search-bar-mobile {
        display: flex;
        align-items: center;
        .search-modal-btn {
          font-size: 13px;
          align-items: center;
          display: flex;
          flex-shrink: 0;
          cursor: pointer;

          &.tag-btn {
            &[data-active='true'] {
              color: #01a0ff;
              svg {
                path {
                  fill: #01a0ff;
                }
              }
            }
          }
        }
        .follow-divider {
          height: 10px;
          width: 1px;
          background-color: #e8e8e8;
          margin: 0 7px;
        }
        .search-btn {
          cursor: pointer;
          svg {
            width: 16px;
            height: 16px;
          }
        }
      }
    }
  }
`
